import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "commitments", title: "Our Commitments" },
  { id: "protections", title: "How We Protect Your Data" },
  { id: "incidents", title: "Incident Response" },
  { id: "report", title: "Reporting a Vulnerability" },
  { id: "scope", title: "Out of Scope" },
  { id: "related", title: "Related Policies" },
];

const SecurityCommitments = () => (
  <>
    <Helmet>
      <title>Security — The Emerging Market</title>
      <meta
        name="description"
        content="How The Emerging Market protects user data, responds to incidents, and receives vulnerability reports."
      />
      <link rel="canonical" href="https://emrkt.co/security" />
    </Helmet>
    <LegalLayout
      title="Security"
      description="The controls we run, how we respond to incidents, and how to report a vulnerability."
      lastUpdated="June 5, 2026"
      effectiveDate="June 5, 2026"
      sections={sections}
    >
      <section id="commitments">
        <h2>1. Our Commitments</h2>
        <p>
          The Emerging Market ("TEM," "we") is a civic-data platform serving Alabama
          communities and the agencies, funders, and advocates that work alongside them.
          We take the trust placed in us seriously and treat security as an operational
          discipline, not a checkbox.
        </p>
        <p>
          This page describes the controls we run today and the commitments we make to
          users, partners, and regulators. It complements our{" "}
          <a href="/privacy">Privacy Policy</a>,{" "}
          <a href="/dpa">Data Processing Agreement</a>, and{" "}
          <a href="/retention">Records Retention Policy</a>.
        </p>
      </section>

      <section id="protections">
        <h2>2. How We Protect Your Data</h2>
        <ul>
          <li>
            <strong>Row-Level Security on every table.</strong> Our database enforces
            access at the row level, scoped to the authenticated user or to a verified
            admin role. There is no shared "logged-in user can see everything" mode.
          </li>
          <li>
            <strong>Role-based admin access.</strong> Admin actions check a server-side
            role function. Roles are stored in a separate table, never on user profiles,
            to prevent privilege-escalation paths.
          </li>
          <li>
            <strong>Secrets in a managed vault.</strong> API keys and service
            credentials are stored in our backend's encrypted secret store, never in
            client code or version control.
          </li>
          <li>
            <strong>Encrypted in transit and at rest.</strong> All traffic uses TLS;
            the database is encrypted at rest by our hosting provider.
          </li>
          <li>
            <strong>Backups and restore drills.</strong> The database is backed up
            daily and we run quarterly restore drills to verify recoverability.
          </li>
          <li>
            <strong>Error monitoring.</strong> Frontend and backend errors are
            captured in Sentry so we see regressions quickly, with personally
            identifiable information stripped from event payloads where practical.
          </li>
          <li>
            <strong>MFA for users.</strong> Members can enable a second factor and
            generate recovery codes from{" "}
            <a href="/account">Account Settings</a>.
          </li>
          <li>
            <strong>Least-privilege automation.</strong> Edge functions use scoped
            credentials and are deployed from version-controlled source.
          </li>
        </ul>
      </section>

      <section id="incidents">
        <h2>3. Incident Response</h2>
        <p>
          We maintain a written incident-response runbook covering security
          (data breach, unauthorized access, leaked secrets), availability (outages and
          degraded service), and abuse on the Ideas Board. The runbook defines severity
          levels, containment playbooks per scenario, communication templates, and a
          post-mortem requirement within 7 days of resolution.
        </p>
        <p>
          On confirmed incidents affecting personal data, we notify affected users — and
          applicable supervisory authorities where required — <strong>without undue delay,
          targeting 72 hours from confirmation</strong>. This aligns with the commitments
          in our <a href="/dpa">Data Processing Agreement</a>. Availability incidents are
          posted in near-real-time on our <a href="/status">status page</a>.
        </p>
        <p>
          You can view our full <a href="/incident-response">Incident Response</a> page
          or <a href="/incident-response#report">report an incident directly</a>.
        </p>
        <p>
          A single platform lead currently acts as Incident Commander, Communications
          Lead, and Remediation Lead. As the team grows we will publish named on-call
          rotations here.
        </p>
      </section>

      <section id="report">
        <h2>4. Reporting a Vulnerability</h2>
        <p>
          If you believe you've found a security vulnerability in emrkt.co, please email{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> with the subject line
          "Security report." Please include:
        </p>
        <ul>
          <li>A clear description of the issue and its potential impact.</li>
          <li>Steps to reproduce, including any proof-of-concept code or screenshots.</li>
          <li>The URL(s), account(s), or component(s) involved.</li>
        </ul>
        <p>
          <strong>Acknowledgement window:</strong> we aim to acknowledge reports within
          3 business days, with a remediation timeline in the same response.
        </p>
        <p>
          <strong>Safe-harbor:</strong> we will not pursue legal action against
          researchers who report vulnerabilities in good faith, follow responsible
          disclosure, avoid privacy violations, do not degrade service, and refrain from
          accessing or modifying data that does not belong to them.
        </p>
      </section>

      <section id="scope">
        <h2>5. Out of Scope</h2>
        <p>
          The following are generally out of scope for our security program. We may still
          review reports, but they do not qualify for acknowledgement or prioritization:
        </p>
        <ul>
          <li>Social engineering of staff, contractors, or users.</li>
          <li>Physical attacks on offices, devices, or infrastructure.</li>
          <li>Denial-of-service attacks and volumetric load testing.</li>
          <li>Automated scanner output without a working proof-of-concept.</li>
          <li>Missing best-practice HTTP headers without a demonstrated exploit.</li>
          <li>Issues affecting only outdated browsers or rooted devices.</li>
        </ul>
      </section>

      <section id="related">
        <h2>6. Related Policies</h2>
        <ul>
          <li><a href="/privacy">Privacy Policy</a></li>
          <li><a href="/dpa">Data Processing Agreement</a></li>
          <li><a href="/retention">Records Retention & Deletion Policy</a></li>
          <li><a href="/acceptable-use">Acceptable Use Policy</a></li>
          <li><a href="/incident-response">Incident Response</a></li>
          <li><a href="/status">Service Status</a></li>
        </ul>
        <p className="text-xs italic">
          The commitments on this page describe our operational practices and do not
          constitute legal advice or a contractual warranty. Contractual security terms
          for enterprise and agency customers are set out in our Data Processing
          Agreement.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default SecurityCommitments;